import { useRef, useState, useImperativeHandle } from 'react';
import { AnimatePresence } from 'framer-motion';
import SlideBackground from '@atoms/new/slideBackground';
import useWordCloudLayout, { CANONICAL_WIDTH, CANONICAL_HEIGHT } from './WonderWords/useWordCloudLayout';
import { hashText } from './WonderWords/wordCloudStyles';
import WordChartWord from './WonderWords/WordChartWord';

// DOM + d3-cloud word cloud renderer. Keeps the Highcharts-shaped
// `chartOptions` prop and exposes a minimal Highcharts-shaped ref shim so
// Wordcloud.js's moderation/pause/analytics code keeps working untouched.
const WordChart = ({
  highchartsRef, chartOptions, containerProps, backgroundColor, bgImg, bgImgOpacity
}) => {
  const internalChartRef = useRef(null);
  const chartRef = highchartsRef || internalChartRef;
  const [hiddenWords, setHiddenWords] = useState(() => new Set());

  const words = chartOptions?.series?.[0]?.data || [];
  const wordcloudOptions = chartOptions?.plotOptions?.wordcloud || {};
  const displayStyle = wordcloudOptions.displayStyle || 'chips';
  const spread = wordcloudOptions.spread || 'normal';
  const fontFamily = wordcloudOptions.style?.fontFamily || 'sans-serif';
  const palette = wordcloudOptions.colorByPoint && Array.isArray(wordcloudOptions.colors) && wordcloudOptions.colors.length > 0
    ? wordcloudOptions.colors
    : null;
  const baseColor = wordcloudOptions.color || '#319795';
  const wordColor = (text) => (palette ? palette[hashText(text) % palette.length] : baseColor);

  const {
    containerRef, placedWords, scaleToFit, contentScale, stretchX, stretchY, styleConfig
  } = useWordCloudLayout({
    words, hiddenWords, fontFamily, displayStyle, spread
  });

  // Highcharts-shaped shim for Wordcloud.js's moderation/pause/analytics code.
  useImperativeHandle(chartRef, () => ({
    chart: {
      series: [{
        chart: { delImgs: [] },
        data: placedWords.map((w) => ({
          name: w.text,
          graphic: { hide: () => setHiddenWords((prev) => new Set(prev).add(w.text)) }
        }))
      }]
    }
  }), [placedWords]);

  return (
    <div
      className={'h-full overflow-hidden w-full wordcloud-chart'}
      style={{ backgroundColor, height: '100%' }}
      suppressHydrationWarning={true}
    >
      {bgImg && <SlideBackground bgImg={bgImg} bgImgOpacity={bgImgOpacity} />}

      <div
        ref={containerRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(containerProps?.style || { width: '100%', height: '100%' })
        }}
      >
        <div
          style={{
            position: 'relative',
            width: CANONICAL_WIDTH,
            height: CANONICAL_HEIGHT,
            flex: 'none',
            transform: `scale(${scaleToFit * contentScale})`
          }}
        >
          <AnimatePresence>
            {placedWords.map((word) => (
              <WordChartWord
                key={word.text}
                word={word}
                color={wordColor(word.text)}
                fontFamily={fontFamily}
                hasChip={styleConfig.hasChip}
                stretchX={stretchX}
                stretchY={stretchY}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default WordChart;
